import React from "react";
import { Snackbar, Alert } from "@mui/material";

/**
 * Transient feedback toast — consolidates the raw MUI Snackbar + filled Alert
 * pattern repeated across the admin action buttons.
 *
 * Brand-styled: MUI supplies positioning, auto-hide, the severity icon, and the
 * close affordance; the fill colors, type, radius, and shadow are brand tokens
 * (teal success / red error / gold warning / navy info).
 *
 * Props:
 *   open, onClose, message
 *   severity: "success" | "info" | "warning" | "error"  (default "success")
 *   autoHideDuration: ms (default 6000)
 *   anchorOrigin: MUI Snackbar anchor (optional)
 */
const TONES = {
  success: { bg: "var(--color-teal, #2ec4b6)", fg: "#ffffff" },
  info: { bg: "var(--color-navy-deep, #0f3460)", fg: "#ffffff" },
  warning: { bg: "var(--color-gold-orange, #ffb454)", fg: "#1c1c1c" },
  error: { bg: "var(--color-error, #bc1b1b)", fg: "#ffffff" },
};

const Toast = ({
  open,
  onClose,
  message,
  severity = "success",
  autoHideDuration = 6000,
  anchorOrigin,
}) => {
  const tone = TONES[severity] || TONES.success;
  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
    >
      <Alert
        variant="filled"
        severity={severity}
        onClose={onClose}
        sx={{
          fontFamily: "var(--font-body, 'Helvetica Neue', Arial, sans-serif)",
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: "0.01em",
          borderRadius: "var(--radius-md, 8px)",
          bgcolor: tone.bg,
          color: tone.fg,
          boxShadow: "var(--shadow-pop, 0 8px 24px rgba(15,23,42,0.14))",
          "& .MuiAlert-icon": { color: tone.fg },
          "& .MuiAlert-action": { color: tone.fg },
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default Toast;
